import { formatMoney } from '@theme/money-formatting';

class KpProductShowcase extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {Array<Record<string, any>>} */
  #variants = [];

  /** @type {number} */
  #activeMediaIndex = 0;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    this.#variants = this.#readVariants();

    for (const trigger of this.querySelectorAll('[data-kp-media-trigger]')) {
      trigger.addEventListener('click', () => this.#activateMediaById(trigger.dataset.mediaId), { signal });
    }

    this.querySelector('[data-kp-media-previous]')?.addEventListener('click', () => this.#moveMedia(-1), { signal });
    this.querySelector('[data-kp-media-next]')?.addEventListener('click', () => this.#moveMedia(1), { signal });

    for (const select of this.querySelectorAll('[data-kp-option]')) {
      select.addEventListener('change', () => this.#updateSelectedVariant(), { signal });
    }

    this.querySelector('[data-kp-quantity-minus]')?.addEventListener('click', () => this.#changeQuantity(-1), {
      signal,
    });
    this.querySelector('[data-kp-quantity-plus]')?.addEventListener('click', () => this.#changeQuantity(1), {
      signal,
    });

    const activeTrigger = this.querySelector('[data-kp-media-trigger][aria-pressed="true"]');
    if (activeTrigger instanceof HTMLElement) this.#activateMediaById(activeTrigger.dataset.mediaId, false);

    this.#updateSelectedVariant();
    this.#startCountdown(signal);
    this.#loadComplementary(signal);
  }

  disconnectedCallback() {
    this.#controller?.abort();
  }

  /** @returns {Array<Record<string, any>>} */
  #readVariants() {
    const source = this.querySelector('[data-kp-variants]');
    if (!(source instanceof HTMLScriptElement)) return [];

    try {
      const variants = JSON.parse(source.textContent || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch {
      return [];
    }
  }

  /** @param {AbortSignal} signal */
  #startCountdown(signal) {
    const countdown = this.querySelector('[data-kp-countdown]');
    if (!(countdown instanceof HTMLElement)) return;

    const endTime = Date.parse(countdown.dataset.end || '');
    if (!Number.isFinite(endTime)) {
      countdown.hidden = true;
      return;
    }

    const days = countdown.querySelector('[data-kp-countdown-days]');
    const hours = countdown.querySelector('[data-kp-countdown-hours]');
    const minutes = countdown.querySelector('[data-kp-countdown-minutes]');
    const seconds = countdown.querySelector('[data-kp-countdown-seconds]');
    if (![days, hours, minutes, seconds].every((item) => item instanceof HTMLElement)) return;

    let timer = 0;
    const update = () => {
      const remaining = endTime - Date.now();
      if (remaining <= 0) {
        countdown.hidden = true;
        if (timer) window.clearInterval(timer);
        return;
      }

      const dayValue = Math.floor(remaining / 86_400_000);
      const hourValue = Math.floor((remaining % 86_400_000) / 3_600_000);
      const minuteValue = Math.floor((remaining % 3_600_000) / 60_000);
      const secondValue = Math.floor((remaining % 60_000) / 1_000);
      const format = (value) => String(value).padStart(2, '0');

      days.textContent = format(dayValue);
      hours.textContent = format(hourValue);
      minutes.textContent = format(minuteValue);
      seconds.textContent = format(secondValue);
    };

    update();
    if (!countdown.hidden) timer = window.setInterval(update, 1_000);
    signal.addEventListener('abort', () => window.clearInterval(timer), { once: true });
  }

  /**
   * Loads per-product complementary recommendations from Shopify and places them inside the purchase panel.
   * @param {AbortSignal} signal
   */
  async #loadComplementary(signal) {
    const container = this.querySelector('[data-kp-complementary]');
    const list = this.querySelector('[data-kp-complementary-list]');
    const recommendationsUrl = this.dataset.recommendationsUrl;
    const sectionId = this.dataset.sectionId;
    const productId = this.dataset.productId;

    if (!(container instanceof HTMLElement) || !(list instanceof HTMLElement)) return;
    if (!recommendationsUrl || !sectionId || !productId) return;

    try {
      const endpoint = new URL(recommendationsUrl, window.location.origin);
      if (endpoint.origin !== window.location.origin) return;

      const limit = Math.min(Math.max(Number.parseInt(this.dataset.complementaryLimit || '2', 10) || 2, 1), 3);
      endpoint.searchParams.set('section_id', sectionId);
      endpoint.searchParams.set('product_id', productId);
      endpoint.searchParams.set('limit', String(limit));
      endpoint.searchParams.set('intent', 'complementary');

      const response = await fetch(endpoint, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
        signal,
      });
      if (!response.ok) return;

      const html = await response.text();
      if (signal.aborted || !html.trim()) return;

      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const source = documentFragment.querySelector('[data-kp-complementary-list]');
      if (!(source instanceof HTMLElement) || source.children.length === 0) return;

      const items = Array.from(source.children, (item) => document.importNode(item, true));
      list.replaceChildren(...items);
      container.hidden = false;
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    } finally {
      container.removeAttribute('aria-busy');
    }
  }

  #updateSelectedVariant() {
    if (this.#variants.length === 0) return;

    const selectedOptions = [...this.querySelectorAll('[data-kp-option]')]
      .filter((select) => select instanceof HTMLSelectElement)
      .map((select) => select.value);

    const variant = this.#variants.find((candidate) => {
      if (selectedOptions.length === 0) return String(candidate.id) === this.#variantIdInput()?.value;
      return candidate.options?.every((value, index) => value === selectedOptions[index]);
    });

    this.#applyVariant(variant);
  }

  /**
   * @param {Record<string, any> | undefined} variant
   */
  #applyVariant(variant) {
    const input = this.#variantIdInput();

    if (!variant) {
      if (input) input.value = '';
      const price = this.querySelector('[data-kp-price]');
      const compareAtPrice = this.querySelector('[data-kp-compare-price]');
      if (price instanceof HTMLElement) price.textContent = this.dataset.unavailableLabel || 'Unavailable';
      if (compareAtPrice instanceof HTMLElement) {
        compareAtPrice.hidden = true;
        compareAtPrice.textContent = '';
      }
      this.#updateAddButton(false, this.dataset.unavailableLabel || 'Unavailable');
      this.#updateInventory(false);
      this.#updateSku('');
      return;
    }

    if (input) {
      input.value = String(variant.id);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.#updatePrice(variant);
    this.#updateInventory(Boolean(variant.available));
    this.#updateSku(variant.sku);
    this.#updateQuantityRules(variant.quantity_rule);

    const addLabel = variant.available
      ? this.dataset.addLabel || 'Add to cart'
      : this.dataset.soldOutLabel || 'Sold out';
    this.#updateAddButton(Boolean(variant.available), addLabel);

    const mediaId = variant.featured_media?.id || variant.featured_image?.id;
    if (mediaId) this.#activateMediaById(String(mediaId), false);

    const addContainer = this.querySelector('[data-kp-add-container]');
    const imageUrl = this.#safeImageUrl(variant.featured_image?.src || variant.featured_image);
    if (addContainer instanceof HTMLElement) {
      addContainer.dataset.productVariantMedia = imageUrl;
    }
  }

  /** @returns {HTMLInputElement | null} */
  #variantIdInput() {
    const input = this.querySelector('[data-kp-variant-id]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @param {Record<string, any>} variant */
  #updatePrice(variant) {
    const price = this.querySelector('[data-kp-price]');
    if (price instanceof HTMLElement) price.textContent = this.#formatPrice(variant.price);

    const compareAtPrice = this.querySelector('[data-kp-compare-price]');
    if (!(compareAtPrice instanceof HTMLElement)) return;

    const compareAtValue = Number(variant.compare_at_price || 0);
    const priceValue = Number(variant.price || 0);
    const showCompareAt = Number.isFinite(compareAtValue) && compareAtValue > priceValue;

    compareAtPrice.hidden = !showCompareAt;
    compareAtPrice.textContent = showCompareAt ? this.#formatPrice(compareAtValue) : '';
  }

  /**
   * @param {boolean} available
   * @param {string} label
   */
  #updateAddButton(available, label) {
    const button = this.querySelector('[data-kp-add-button]');
    const labelElement = this.querySelector('[data-kp-add-label]');
    const dynamicCheckout = this.querySelector('[data-kp-dynamic-checkout]');

    if (button instanceof HTMLButtonElement) button.disabled = !available;
    if (labelElement instanceof HTMLElement) {
      const icon = labelElement.querySelector('.kp-product-showcase__add-icon');
      labelElement.replaceChildren();
      if (icon) labelElement.append(icon);
      labelElement.append(document.createTextNode(label));
    }
    if (dynamicCheckout instanceof HTMLElement) dynamicCheckout.hidden = !available;
  }

  /** @param {boolean} available */
  #updateInventory(available) {
    const inventory = this.querySelector('[data-kp-inventory]');
    const label = this.querySelector('[data-kp-inventory-label]');
    if (!(inventory instanceof HTMLElement) || !(label instanceof HTMLElement)) return;

    inventory.classList.toggle('kp-product-showcase__inventory--out', !available);
    label.textContent = available
      ? this.dataset.inStockLabel || 'In stock'
      : this.dataset.outOfStockLabel || 'Out of stock';
  }

  /** @param {unknown} sku */
  #updateSku(sku) {
    const wrapper = this.querySelector('[data-kp-sku-wrapper]');
    const value = this.querySelector('[data-kp-sku]');
    if (!(wrapper instanceof HTMLElement) || !(value instanceof HTMLElement)) return;

    const normalizedSku = typeof sku === 'string' ? sku.trim() : '';
    value.textContent = normalizedSku;
    wrapper.classList.toggle('hidden', normalizedSku.length === 0);
  }

  /** @param {Record<string, any> | undefined} rule */
  #updateQuantityRules(rule) {
    const input = this.querySelector('[data-kp-quantity]');
    if (!(input instanceof HTMLInputElement)) return;

    const minimum = this.#positiveNumber(rule?.min, 1);
    const increment = this.#positiveNumber(rule?.increment, 1);
    const maximum = this.#positiveNumber(rule?.max, 0);

    input.min = String(minimum);
    input.step = String(increment);
    if (maximum > 0) input.max = String(maximum);
    else input.removeAttribute('max');

    const current = this.#positiveNumber(input.value, minimum);
    const stepsFromMinimum = Math.max(0, Math.ceil((current - minimum) / increment));
    const normalized = minimum + stepsFromMinimum * increment;
    input.value = String(Math.min(normalized, maximum > 0 ? maximum : Number.MAX_SAFE_INTEGER));
  }

  /** @param {number} direction */
  #changeQuantity(direction) {
    const input = this.querySelector('[data-kp-quantity]');
    if (!(input instanceof HTMLInputElement)) return;

    const minimum = this.#positiveNumber(input.min, 1);
    const increment = this.#positiveNumber(input.step, 1);
    const maximum = this.#positiveNumber(input.max, 0);
    const current = this.#positiveNumber(input.value, minimum);
    const next = current + increment * direction;
    const bounded = Math.min(Math.max(next, minimum), maximum > 0 ? maximum : Number.MAX_SAFE_INTEGER);

    input.value = String(bounded);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * @param {unknown} value
   * @param {number} fallback
   * @returns {number}
   */
  #positiveNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  }

  /**
   * @param {string | undefined} mediaId
   * @param {boolean} focusThumbnail
   */
  #activateMediaById(mediaId, focusThumbnail = false) {
    if (!mediaId) return;

    const panels = [...this.querySelectorAll('[data-kp-media-panel]')];
    const index = panels.findIndex((panel) => panel instanceof HTMLElement && panel.dataset.mediaId === mediaId);
    if (index < 0) return;

    this.#activateMedia(index, focusThumbnail);
  }

  /**
   * @param {number} index
   * @param {boolean} focusThumbnail
   */
  #activateMedia(index, focusThumbnail = false) {
    const panels = [...this.querySelectorAll('[data-kp-media-panel]')];
    const triggers = [...this.querySelectorAll('[data-kp-media-trigger]')];
    if (panels.length === 0) return;

    const normalizedIndex = ((index % panels.length) + panels.length) % panels.length;
    this.#activeMediaIndex = normalizedIndex;

    panels.forEach((panel, panelIndex) => {
      if (!(panel instanceof HTMLElement)) return;
      const active = panelIndex === normalizedIndex;
      panel.hidden = !active;

      if (!active) {
        for (const video of panel.querySelectorAll('video')) video.pause();
      }
    });

    triggers.forEach((trigger, triggerIndex) => {
      if (!(trigger instanceof HTMLButtonElement)) return;
      const active = triggerIndex === normalizedIndex;
      trigger.setAttribute('aria-pressed', String(active));
      if (active) {
        trigger.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
        if (focusThumbnail) trigger.focus({ preventScroll: true });
      }
    });

    const status = this.querySelector('[data-kp-media-status]');
    if (status instanceof HTMLElement) {
      const template = this.dataset.slideStatus || 'Slide __INDEX__ of __LENGTH__';
      status.textContent = template
        .replace('__INDEX__', String(normalizedIndex + 1))
        .replace('__LENGTH__', String(panels.length));
    }
  }

  /** @param {number} direction */
  #moveMedia(direction) {
    this.#activateMedia(this.#activeMediaIndex + direction, false);
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #safeImageUrl(value) {
    if (typeof value !== 'string' || value.length === 0) return '';

    try {
      const url = new URL(value, window.location.origin);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url.href : '';
    } catch {
      return '';
    }
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  #formatPrice(value) {
    const amount = Number(value);
    if (!Number.isFinite(amount)) return '';

    const currency = this.dataset.currency || 'USD';
    const moneyFormat = this.dataset.moneyFormat || '{{amount}}';

    try {
      return formatMoney(amount, moneyFormat, currency);
    } catch {
      const locale = this.dataset.locale || document.documentElement.lang || 'en';
      const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency });
      const precision = formatter.resolvedOptions().maximumFractionDigits;
      return formatter.format(amount / 10 ** precision);
    }
  }
}

if (!customElements.get('kp-product-showcase')) {
  customElements.define('kp-product-showcase', KpProductShowcase);
}
